import React, { useState } from 'react';
import { Box, Typography, Chip, Paper, IconButton } from '@mui/material';
import { Edit, ContentCopy, Delete } from '@mui/icons-material';

/**
 * ClockWheel — pie/donut visualization of a clock template hour.
 *
 * Each segment is sized proportionally to its duration as a percentage of the
 * target hour (3600s). Zero-duration items (timers/markers) are drawn as thin
 * radial markers at their position in the rotation. Unscheduled time is shown
 * as a grey remainder slice.
 *
 * Props:
 *  - segments: [{ id, label, sublabel, color, duration, required, isMarker }]
 *  - targetDuration: seconds for the full circle (default 3600)
 *  - onEdit(id), onDuplicate(id), onDelete(id): optional callbacks
 */

const SIZE = 340;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R_OUTER = 150;
const R_INNER = 62;
const TICK_R = 158;

function polar(cx, cy, r, angleDeg) {
  // 0° = 12 o'clock, clockwise
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function donutPath(startAngle, endAngle) {
  const sweep = endAngle - startAngle;
  if (sweep >= 359.999) {
    // Full circle: two half-arcs to avoid degenerate path
    return donutPath(startAngle, startAngle + 180) + ' ' + donutPath(startAngle + 180, startAngle + 360);
  }
  const [x1, y1] = polar(CX, CY, R_OUTER, startAngle);
  const [x2, y2] = polar(CX, CY, R_OUTER, endAngle);
  const [x3, y3] = polar(CX, CY, R_INNER, endAngle);
  const [x4, y4] = polar(CX, CY, R_INNER, startAngle);
  const largeArc = sweep > 180 ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${R_OUTER} ${R_OUTER} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${R_INNER} ${R_INNER} 0 ${largeArc} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

function formatSeconds(secs) {
  const s = Math.round(secs);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

export default function ClockWheel({ segments = [], targetDuration = 3600, onEdit, onDuplicate, onDelete }) {
  const [hoveredId, setHoveredId] = useState(null);

  const totalDuration = segments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
  // If the clock runs over the hour, scale the circle to the full total so
  // everything remains visible; otherwise the circle represents the hour.
  const circleDuration = Math.max(totalDuration, targetDuration);
  const degPerSec = 360 / circleDuration;

  // Build slices with running start angles (position order = rotation order)
  let cursor = 0;
  const slices = segments.map((seg, index) => {
    const startAngle = cursor * degPerSec;
    const dur = seg.duration || 0;
    cursor += dur;
    const endAngle = cursor * degPerSec;
    const pct = totalDuration > 0 ? (dur / circleDuration) * 100 : 0;
    return { ...seg, index, startAngle, endAngle, pct };
  });

  const remainder = targetDuration - totalDuration;
  const remainderStart = cursor * degPerSec;

  // Minute ticks every 5 minutes (relative to circleDuration so ticks stay
  // meaningful when the hour is exactly filled; only draw when circle ≈ hour)
  const showTicks = circleDuration <= targetDuration * 1.001;
  const ticks = [];
  if (showTicks) {
    for (let min = 0; min < 60; min += 5) {
      ticks.push((min * 60 * 360) / circleDuration);
    }
  }

  const isOver = totalDuration > targetDuration + 0.5;
  const isUnder = totalDuration < targetDuration - 0.5;

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Wheel */}
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          {/* Background ring */}
          <circle cx={CX} cy={CY} r={(R_OUTER + R_INNER) / 2} fill="none" stroke="var(--rs-border)" strokeWidth={R_OUTER - R_INNER} />

          {/* Item slices */}
          {slices.map((slice) => {
            if (!slice.duration) return null;
            const dim = hoveredId !== null && hoveredId !== slice.id;
            return (
              <path
                key={`slice-${slice.id ?? slice.index}`}
                d={donutPath(slice.startAngle, slice.endAngle)}
                fill={slice.color}
                fillOpacity={dim ? 0.35 : 0.9}
                stroke="rgba(0,0,0,0.5)"
                strokeWidth={1}
                style={{ cursor: onEdit ? 'pointer' : 'default', transition: 'fill-opacity 0.15s' }}
                onMouseEnter={() => setHoveredId(slice.id ?? slice.index)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => onEdit && onEdit(slice.id)}
              >
                <title>{`#${slice.index + 1} ${slice.label} — ${formatSeconds(slice.duration)} (${slice.pct.toFixed(1)}%)`}</title>
              </path>
            );
          })}

          {/* Remainder (unscheduled time) */}
          {remainder > 0.5 && (
            <path
              d={donutPath(remainderStart, 360)}
              fill="var(--rs-surface)"
              stroke="rgba(0,0,0,0.5)"
              strokeWidth={1}
            >
              <title>{`Unscheduled — ${formatSeconds(remainder)}`}</title>
            </path>
          )}

          {/* Zero-duration markers (timers) drawn as radial lines */}
          {slices.map((slice) => {
            if (slice.duration || !slice.isMarker) return null;
            const [x1, y1] = polar(CX, CY, R_INNER - 4, slice.startAngle);
            const [x2, y2] = polar(CX, CY, R_OUTER + 6, slice.startAngle);
            return (
              <line
                key={`marker-${slice.id ?? slice.index}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={slice.color}
                strokeWidth={2.5}
                strokeDasharray="4 3"
              >
                <title>{`#${slice.index + 1} ${slice.label}`}</title>
              </line>
            );
          })}

          {/* 5-minute ticks + labels */}
          {ticks.map((angle, i) => {
            const [x1, y1] = polar(CX, CY, R_OUTER + 1, angle);
            const [x2, y2] = polar(CX, CY, TICK_R, angle);
            const [lx, ly] = polar(CX, CY, TICK_R + 10, angle);
            return (
              <g key={`tick-${i}`}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--rs-muted)" strokeWidth={i % 3 === 0 ? 2 : 1} />
                {i % 3 === 0 && (
                  <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" fontSize="10" fill="var(--rs-muted)">
                    :{String((i * 5) % 60).padStart(2, '0')}
                  </text>
                )}
              </g>
            );
          })}

          {/* Center summary */}
          <text x={CX} y={CY - 12} textAnchor="middle" fontSize="18" fontWeight="bold" fill={isOver ? '#ff6b6b' : (isUnder ? '#ffb74d' : '#66bb6a')}>
            {formatSeconds(totalDuration)}
          </text>
          <text x={CX} y={CY + 8} textAnchor="middle" fontSize="11" fill="var(--rs-muted)">
            of {formatSeconds(targetDuration)}
          </text>
          <text x={CX} y={CY + 26} textAnchor="middle" fontSize="11" fill="var(--rs-muted)">
            {((totalDuration / targetDuration) * 100).toFixed(1)}%
          </text>
        </svg>
      </Box>

      {/* Legend */}
      <Box sx={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
        {slices.map((slice) => {
          const hovered = hoveredId === (slice.id ?? slice.index);
          return (
            <Paper
              key={`legend-${slice.id ?? slice.index}`}
              onMouseEnter={() => setHoveredId(slice.id ?? slice.index)}
              onMouseLeave={() => setHoveredId(null)}
              sx={{
                p: 1,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: hovered ? 'var(--rs-row)' : 'transparent',
                border: '1px solid var(--rs-border)',
                transition: 'background-color 0.15s',
              }}
            >
              <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: slice.color, flexShrink: 0, border: slice.isMarker && !slice.duration ? '1px dashed var(--rs-muted)' : 'none' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: hovered ? 'bold' : 'normal' }}>
                  #{slice.index + 1} · {slice.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {slice.duration
                    ? `${formatSeconds(slice.duration)} · ${slice.pct.toFixed(1)}%`
                    : 'Marker (no duration)'}
                  {slice.sublabel ? ` · ${slice.sublabel}` : ''}
                </Typography>
              </Box>
              {slice.required && (
                <Chip label="Required" size="small" sx={{ bgcolor: '#4caf50', color: '#fff', fontSize: '0.6rem', height: '18px' }} />
              )}
              {onDuplicate && (
                <IconButton size="small" onClick={() => onDuplicate(slice.id)} sx={{ color: '#9e9e9e' }} title="Duplicate">
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {onEdit && (
                <IconButton size="small" onClick={() => onEdit(slice.id)} sx={{ color: '#22d3ee' }} title="Edit">
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton size="small" onClick={() => onDelete(slice.id)} sx={{ color: '#ff6b6b' }} title="Delete">
                  <Delete sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Paper>
          );
        })}
        {remainder > 0.5 && (
          <Paper sx={{ p: 1, px: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'transparent', border: '1px dashed var(--rs-border)' }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: 'var(--rs-surface)', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              Unscheduled · {formatSeconds(remainder)} · {((remainder / targetDuration) * 100).toFixed(1)}%
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
